<template>
  <div
    :ref="`origin_destination:${origin_destination_index}`"
    :class="`origin-destination ${full_width ? 'origin-destination--full-width' : ''}`"
  >
    <!-- Hidden input for focus management -->
    <button
      :ref="`hidden_input:${origin_destination_index}`"
      class="focus-dump-button"
    ></button>

    <b-col
      v-if="full_width"
      cols="12"
      class="origin-destination__title origin-destination--mobile"
    >
      <span
        v-b-toggle="`origin_destination_${origin_destination_index}`"
      >Volo {{ origin_destination_index + 1 }}</span>
      <button
        v-if="origin_destination_count > 2"
        class="btn p-0"
        role="button"
        type="button"
        @click="() => $emit('remove', origin_destination_index)"
      >
        <vb-icon name="cross_circle" :size="20" :color="sass('info')" />
      </button>
    </b-col>

    <component
      :is="collapse_binding.is"
      v-bind="collapse_binding"
      class="origin-destination__collapse"
    >
      <b-row>
        <!-- Departure Airport -->
        <b-col class="origin-destination__departure" cols="12" lg="4">
          <div class="vb-form">
            <airport-search-input
              :value="departure_airport"
              :placeholder="departure_airport_placeholder"
              default_placeholder="Da dove?"
              icon="air_start"
              :ref_name="`autocomplete-departure:${origin_destination_index}`"
              :filtered_iata_codes="departure_filtered_airports"
              @select="on_submit_departure_airport"
              @focus="on_airport_focus"
              @blur="autocomplete_focus = false"
            />
          </div>

          <button
            v-if="is_origin_destination_valid"
            class="origin-destination__reverse"
            role="button"
            type="button"
            @click="invert_origin_destinations"
          >
            <vb-icon name="reverse" :size="18" />
          </button>
        </b-col>

        <!-- Arrival Airport -->
        <b-col class="origin-destination__arrival" cols="12" lg="4">
          <div class="vb-form">
            <airport-search-input
              :value="arrival_airport"
              :placeholder="arrival_airport_placeholder"
              default_placeholder="A dove?"
              icon="air_arrival"
              :ref_name="`autocomplete-arrival:${origin_destination_index}`"
              :filtered_iata_codes="arrival_filtered_airports"
              @select="on_submit_arrival_airport"
              @focus="on_airport_focus"
              @blur="autocomplete_focus = false"
            />
          </div>
        </b-col>

        <!-- Date Range Picker (Departure + Return) -->
        <b-col class="origin-destination__date-range-picker" cols="12" lg="4">
          <date-range-picker
            :dates.sync="flight_dates"
            :range="return_flight"
            :min_date="departure_min_date"
            :max_date="max_date_value"
            :disclaimer="disclaimer"
          />

          <button
            v-if="origin_destination_count > 2"
            class="btn origin-destination--desktop"
            role="button"
            type="button"
            @click="() => $emit('remove', origin_destination_index)"
          >
            <vb-icon name="cross_circle" :size="20" :color="sass('info')" />
          </button>
        </b-col>
      </b-row>
    </component>
  </div>
</template>

<script>
  import AirportSearchInput from './airport-search-input.vue';
  import DateRangePicker from './date-range-picker.vue';
  import { mapState } from 'vuex';
  import { toDate } from '@liveflight/utilities/dates';
  import { calculateMinDepartureDate, calculateMaxDate } from './logic';
  import {
    TRIP_TYPES,
    is_origin_destination_valid,
    create_origin_destination,
  } from '@liveflight/utilities/origin-destination';
  import { get_config } from '@/utilities/';

  export default {
    name: 'origin-destination',
    components: {
      'airport-search-input': AirportSearchInput,
      'date-range-picker': DateRangePicker,
    },
    props: {
      // Departure location { iata_code, label }
      departure: {
        type: Object,
        default: () => ({ iata_code: '', label: '' }),
      },
      // Arrival location { iata_code, label }
      arrival: {
        type: Object,
        default: () => ({ iata_code: '', label: '' }),
      },
      // Dates array for date-range-picker [departure_date, return_date?]
      dates: {
        type: Array,
        default: () => ['', ''],
      },
      // Index of this origin-destination in the array
      origin_destination_index: {
        type: Number,
        required: true,
      },
      // Total number of origin-destinations (for conditional rendering)
      origin_destination_count: {
        type: Number,
        default: 1,
      },
      // Whether this is a return flight
      return_flight: {
        type: Boolean,
        default: false,
      },
      // Overrides constraints other than base logic
      date_constraints: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        TRIP_TYPES,
        autocomplete_focus: false,
      };
    },
    computed: {
      ...mapState('liveflight', ['trip_type']),
      full_width() {
        return this.trip_type === TRIP_TYPES.MULTI_ITINERARY;
      },
      // Computed getters using props - extract iata_code for display
      departure_airport() {
        const { iata_code = '' } = this.departure || {};
        return iata_code;
      },
      departure_airport_placeholder() {
        const { label } = this.departure || {};
        return label || 'Da dove?';
      },
      departure_filtered_airports() {
        if (
          get_config(
            'guiSettings.customization.allowSameOriginAndDestination',
            false
          )
        ) {
          return {};
        }
        return {
          [this.arrival_airport]: this.arrival_airport.length > 0,
        };
      },
      arrival_airport() {
        const { iata_code = '' } = this.arrival || {};
        return iata_code;
      },
      arrival_airport_placeholder() {
        const { label } = this.arrival || {};
        return label || 'A dove?';
      },
      arrival_filtered_airports() {
        if (
          get_config(
            'guiSettings.customization.allowSameOriginAndDestination',
            false
          )
        ) {
          return {};
        }
        return {
          [this.departure_airport]: this.departure_airport.length > 0,
        };
      },
      // Bridge for date-range-picker
      flight_dates: {
        get() {
          return this.dates;
        },
        set(dates) {
          this.$emit('update:dates', dates);
        },
      },
      departure_min_date() {
        return (
          this.date_constraints.departure_min_date ||
          calculateMinDepartureDate()
        );
      },
      return_min_date() {
        const dep_date = this.dates[0];
        return dep_date ? toDate(dep_date) : new Date();
      },
      max_date_value() {
        return this.date_constraints.max_date || calculateMaxDate();
      },
      disclaimer() {
        return get_config('guiSettings.minDisclaimer', '');
      },
      collapse_binding() {
        if (!this.is_mobile()) {
          return { is: 'div' };
        }
        if (this.trip_type !== TRIP_TYPES.MULTI_ITINERARY) {
          return { is: 'div' };
        }
        return {
          is: 'b-collapse',
          id: `origin_destination_${this.origin_destination_index}`,
          accordion: 'my-accordion',
          visible:
            this.origin_destination_index === 0 &&
            this.$route.name === 'homepage',
        };
      },
      is_origin_destination_valid() {
        return is_origin_destination_valid(
          create_origin_destination({
            departure: this.departure,
            arrival: this.arrival,
            date: this.dates[0],
          })
        ).valid;
      },
    },
    methods: {
      on_airport_focus() {
        this.autocomplete_focus = true;
        // Scrolling is centralized in the parent booking-engine so it can be
        // gated to first-focus-only across all origin-destination copies.
        this.$emit('airport-focus');
      },
      on_submit_departure_airport(result) {
        // Sanity check
        if (!result) {
          return;
        }

        const ref = this.$refs[`hidden_input:${this.origin_destination_index}`];
        if (ref && ref.focus) {
          ref.focus({ preventScroll: true });
        }
        this.$emit('update:departure', {
          iata_code: result.iata_code,
          label: result.name_full,
        });
      },
      on_submit_arrival_airport(result) {
        // Sanity check
        if (!result) {
          return;
        }

        const ref = this.$refs[`hidden_input:${this.origin_destination_index}`];
        if (ref && ref.focus) {
          ref.focus({ preventScroll: true });
        }
        this.$emit('update:arrival', {
          iata_code: result.iata_code,
          label: result.name_full,
        });
      },
      invert_origin_destinations() {
        this.$emit('invert', this.origin_destination_index);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .origin-destination {
    position: relative;
    padding-top: $grid-gutter-width / 2;

    @include media-breakpoint-down(lg) {
      border-top: 1px solid $gray-200;
    }

    &:first-child {
      border: none;
      padding-top: 0;
    }

    &__title {
      display: flex;
      justify-content: space-between;
      align-items: center;

      & > span {
        font-weight: $vb-font-weight-semibold;
        color: var(--primary);
      }
    }

    &__departure {
      position: relative;
      z-index: 5;
      padding-bottom: $grid-gutter-width / 2;

      @include media-breakpoint-up(lg) {
        padding-bottom: 0;
      }
    }

    &__arrival {
      position: relative;
      z-index: 4;
      padding-bottom: $grid-gutter-width / 2;

      @include media-breakpoint-up(lg) {
        padding-bottom: 0;
        padding-left: 0 !important;
      }
    }

    &__reverse {
      background-color: $white;
      padding: $grid-gutter-width / 6;
      border: 1px solid var(--primary);
      border-radius: 50%;
      position: absolute;
      z-index: 6;

      ::v-deep svg {
        --color: var(--text);
        color: var(--text);
        fill: var(--text);
      }

      top: $grid-gutter-width / 3;
      right: -$grid-gutter-width / 4;

      @include media-breakpoint-down(md) {
        top: unset;
        bottom: -$grid-gutter-width / 4;
        transform: translateY(0);
        right: $grid-gutter-width;
      }
    }

    &__date-range-picker {
      position: relative;
      z-index: 3;
      display: flex;
      margin-bottom: $grid-gutter-width / 2;

      & > :nth-child(1) {
        flex-grow: 1;
      }

      ::v-deep input {
        font-size: $font-size-base;
      }

      @include media-breakpoint-up(lg) {
        padding-bottom: 0;
        padding-left: 0 !important;
        margin-bottom: 0;
      }
    }

    &__collapse {
      width: 100%;
    }

    // Focus button for airport input management
    .focus-dump-button {
      opacity: 0;
      position: absolute;
      left: -999999px;
      top: -999999px;
    }

    &--mobile {
      padding: 0 0 $grid-gutter-width / 3 0;

      @include media-breakpoint-up(lg) {
        display: none !important;
      }
    }

    &--desktop {
      @include media-breakpoint-down(md) {
        display: none !important;
      }
    }
  }
</style>
